import type {
    CellClassParams,
    ColSpanParams,
    ColumnState,
    ICellRendererParams,
    IHeaderParams,
    ITooltipParams,
} from "@/ui/molecules/Grid";
import GridHeaderComponent from "@/ui/molecules/GridHeaderComponent";

import GridRendererNumber from "@/ui/organisms/GridRendererNumber";

import GridMappingsDropdownItemAccounts, {
    isOmitOption,
} from "./GridMappingsDropdownItemAccounts";
import GridMappingsRendererFirstColumn from "./GridMappingsRendererFirstColumn";

import {
    isRowCategory,
    isRowOmitted,
    isRowTotal,
    isRowWithOperatingStatements,
} from "../typeChecksGrids";
import type { GridMappingsProps } from "./GridMappings";
import type { TAllRowsAvailable } from "../typesGrids";
import { formatNumber, getPinnedRowBackgroundColor } from "../utils";
import type { ExtractionsErrorListProps } from "../ExtractionsSidebar/ExtractionsErrorList";
import { isNotNullable } from "@/utils/guards";

const SPACE_IN_PIXELS_PER_NUMERIC_CHARACTER = 8;
const ADDITIONAL_SPACE_IN_PIXELS = 32;
const calculateColumnWidths = (value: number) => {
    const columnWidth =
        value * SPACE_IN_PIXELS_PER_NUMERIC_CHARACTER +
        ADDITIONAL_SPACE_IN_PIXELS;
    const columnMinWidth = value * SPACE_IN_PIXELS_PER_NUMERIC_CHARACTER;
    return {
        columnWidth,
        columnMinWidth,
    };
};

export const makeGridMappingsColumnDefs = ({
    alteredRowsIds,
    optionsAccounts,
    columnDefsOperatingStatementsFromProps,
    longestOperatingStatementAndTotalsNumericValue,
    errorGroups,
    initialState,
    editMode,
    allowValueEditing,
    setColumnState,
    extractionId,
    selectedColumns,
    onToggleColumnSelection,
}: {
    alteredRowsIds: number[];
    optionsAccounts:
        | GridMappingsProps["optionsAccounts"]
        | (Omit<GridMappingsProps["optionsAccounts"][number], "value"> & {
              value: string | null;
          })[];
    columnDefsOperatingStatementsFromProps: GridMappingsProps["columnDefsOperatingStatements"];
    longestOperatingStatementAndTotalsNumericValue: number;
    errorGroups: ExtractionsErrorListProps["errors"];
    initialState?: ColumnState[];
    editMode: "off" | "rows" | "columns";
    allowValueEditing: boolean;
    setColumnState: (
        extractionId: number,
        state: boolean,
        columnId?: number,
    ) => Promise<void>;
    extractionId: number;
    selectedColumns: number[];
    onToggleColumnSelection: (columnId: number, checked: boolean) => void;
}) => {
    const accountsValues = optionsAccounts.map(option => option.value);
    const optionsAccountsWithoutNullValue = optionsAccounts.filter(
        (option): option is GridMappingsProps["optionsAccounts"][number] =>
            option.value !== null,
    ) as GridMappingsProps["optionsAccounts"];
    const accountsReference = optionsAccountsWithoutNullValue.reduce(
        (acc, option) => ({
            ...acc,
            [option.value]: option.label,
        }),
        {} as Record<string, string>,
    );
    const subcategoryByAccountCode = optionsAccountsWithoutNullValue.reduce(
        (acc, option) => ({
            ...acc,
            [option.value]: option.subcategory ?? null,
        }),
        {} as Record<string, string | null>,
    );
    // Subcategories are optional on a CoA — only show the column when at least
    // one mapped account actually has one, otherwise keep the grid as-is.
    const hasAnySubcategory = optionsAccounts.some(option =>
        isNotNullable(option.subcategory),
    );

    const cellStylesWithEllipsis = {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        display: "block",
    };
    const commonColumnParameters = {
        suppressMovable: true,
        cellStyle: cellStylesWithEllipsis,
    };
    const getPinnedTotalRowTooltip = (data: TAllRowsAvailable | undefined) => {
        if (!data || !isRowTotal(data)) return null;
        return data.pinnedRowTooltip ?? null;
    };

    const getColumnWidth = (
        field: string,
        headerName: string,
        defaultWidth: number,
    ) => {
        if (initialState?.find(s => s.colId === field)) {
            return initialState.find(s => s.colId === field)?.width;
        }
        const headerNameWidth = headerName.length * 8;
        return Math.max(defaultWidth, headerNameWidth);
    };

    const columnDefOriginalAccountName = {
        headerName: "Original Account Name",
        headerComponent: GridHeaderComponent,
        field: "originalAccountName",
        width: 348,
        minWidth: 348,
        ...commonColumnParameters,
        cellStyle: {
            ...commonColumnParameters.cellStyle,
            paddingLeft: "0px",
            paddingRight: "0px",
        },
        cellRenderer: (params: ICellRendererParams<TAllRowsAvailable>) => (
            <GridMappingsRendererFirstColumn
                isTypeAltered={
                    params.data?.originalRowType !== params.data?.rowType
                }
                shouldShowBorder={editMode === "off"}
                {...params}
            />
        ),
        colSpan: (params: ColSpanParams<TAllRowsAvailable>) =>
            params.data && isRowTotal(params.data)
                ? // span the account-name + account-mapping cells, plus the
                  // Sub-Category cell when that column is shown
                  hasAnySubcategory
                    ? 3
                    : 2
                : params.data && isRowCategory(params.data)
                  ? Number.MAX_SAFE_INTEGER // @HACK: This is a hack to make the category row span all columns
                  : 0,
        tooltipValueGetter: (params: ITooltipParams<TAllRowsAvailable>) =>
            getPinnedTotalRowTooltip(params.data),
    };

    const columnDefAccount = {
        headerName: "Account Mapping",
        headerComponent: GridHeaderComponent,
        field: "account",
        editable: true,
        cellEditor: "agRichSelectCellEditor",
        width: 348,
        minWidth: 348,
        cellEditorParams: {
            allowTyping: true,
            filterList: true,
            valueListMaxHeight: 220,
            searchType: "matchAny",
            cellHeight: 48,
            cellRenderer: (
                params: ICellRendererParams<TAllRowsAvailable, string>,
            ) => (
                <GridMappingsDropdownItemAccounts
                    optionsAccounts={optionsAccounts}
                    isRowOmitted={
                        isNotNullable(params.value)
                            ? isOmitOption({
                                  value: params.value,
                              })
                            : false
                    }
                    displayRightIcon={false}
                    {...params}
                />
            ),
            values: accountsValues,
        },
        refData: accountsReference,
        cellRenderer: (params: ICellRendererParams<TAllRowsAvailable>) => (
            <GridMappingsDropdownItemAccounts
                optionsAccounts={optionsAccounts}
                displayRightIcon={true}
                isRowOmitted={params.data ? isRowOmitted(params.data) : false}
                isAltered={
                    params.data?.rowId !== undefined
                        ? alteredRowsIds.includes(Number(params.data?.rowId))
                        : false
                }
                {...params}
            />
        ),
        cellStyle: {
            ...commonColumnParameters.cellStyle,
            paddingRight: "0px",
            paddingLeft: "0px",
        },
    };

    const columnDefSubcategory = {
        headerName: "Sub-Category",
        headerComponent: GridHeaderComponent,
        colId: "subcategory",
        field: "subcategory",
        width: 200,
        minWidth: 200,
        ...commonColumnParameters,
        // The cell value isn't on the row data — derive it from the row's
        // mapped account code. Unmapped/omitted rows fall through to empty.
        valueGetter: (params: { data?: TAllRowsAvailable }) => {
            if (!params.data || !isRowWithOperatingStatements(params.data)) {
                return null;
            }
            const mappedAccountCode = params.data.account;
            return isNotNullable(mappedAccountCode)
                ? subcategoryByAccountCode[mappedAccountCode] ?? null
                : null;
        },
    };

    const columnOperatingStatementWidths = calculateColumnWidths(
        longestOperatingStatementAndTotalsNumericValue,
    );

    const columnDefsOperatingStatements =
        columnDefsOperatingStatementsFromProps.map(
            operatingStatementsColumnDef => {
                const isDisabled =
                    operatingStatementsColumnDef.isDisabled === true;
                const columnId = operatingStatementsColumnDef.columnId;
                const isColumnSelected =
                    isNotNullable(columnId) &&
                    selectedColumns.includes(columnId);

                return {
                    headerName: operatingStatementsColumnDef.headerName,
                    headerComponent: (params: IHeaderParams) => (
                        <GridHeaderComponent
                            {...params}
                            textAlign="center"
                            isDisabled={isDisabled}
                            checkbox={
                                editMode === "columns" &&
                                isNotNullable(columnId)
                                    ? {
                                          checked: isColumnSelected,
                                          onChange: () =>
                                              onToggleColumnSelection(
                                                  columnId,
                                                  !isColumnSelected,
                                              ),
                                      }
                                    : undefined
                            }
                        />
                    ),
                    field: operatingStatementsColumnDef.field,
                    width: getColumnWidth(
                        operatingStatementsColumnDef.field,
                        operatingStatementsColumnDef.headerName,
                        columnOperatingStatementWidths.columnWidth,
                    ),
                    minWidth: columnOperatingStatementWidths.columnMinWidth,
                    // Only account-row cells on enabled columns are correctable.
                    editable: (params: { data?: TAllRowsAvailable }) =>
                        allowValueEditing &&
                        !isDisabled &&
                        params.data?.rowType === "account",
                    cellEditor: "agTextCellEditor",
                    // Edit in dollars (matching the display), not the internal
                    // hundredths value, so typed/pasted input isn't misscaled.
                    cellEditorParams: { useFormatter: true },
                    valueFormatter: (params: { value?: unknown }) =>
                        typeof params.value === "number"
                            ? (params.value / 100).toFixed(2)
                            : "",
                    cellRenderer: (params: ICellRendererParams) => (
                        <GridRendererNumber
                            errorGroups={errorGroups}
                            isDisabled={isDisabled}
                            {...params}
                        />
                    ),
                    tooltipValueGetter: (
                        params: ITooltipParams<TAllRowsAvailable>,
                    ) => {
                        if (!params.data) return null;
                        const pinnedTotalRowTooltip = getPinnedTotalRowTooltip(
                            params.data,
                        );
                        if (pinnedTotalRowTooltip !== null) {
                            return pinnedTotalRowTooltip;
                        }
                        if (!isRowWithOperatingStatements(params.data))
                            return null;
                        const value =
                            params.data[operatingStatementsColumnDef.field];
                        if (typeof value !== "number") return null;

                        return formatNumber(value);
                    },
                    cellStyle: {
                        ...commonColumnParameters.cellStyle,
                        paddingRight: "0px",
                        paddingLeft: "0px",
                    },
                    mainMenuItems: [
                        "resetColumns",
                        "separator",
                        "autoSizeThis",
                        "autoSizeAll",
                        "separator",
                        ...(isNotNullable(operatingStatementsColumnDef.columnId)
                            ? [
                                  {
                                      name: isDisabled
                                          ? "Enable Column"
                                          : "Disable Column",
                                      action: () => {
                                          const { columnId } =
                                              operatingStatementsColumnDef;
                                          if (isNotNullable(columnId)) {
                                              void setColumnState(
                                                  extractionId,
                                                  isDisabled,
                                                  columnId,
                                              );
                                          }
                                      },
                                  },
                              ]
                            : []),
                        {
                            name: "Restore all columns",
                            action: () => {
                                const { columnId } =
                                    operatingStatementsColumnDef;
                                if (isNotNullable(columnId)) {
                                    void setColumnState(extractionId, true);
                                }
                            },
                        },
                    ],
                };
            },
        );

    const cellStyleCheckbox = (params: CellClassParams<TAllRowsAvailable>) => {
        const { data } = params;
        if (!data) return {};
        const pinnedRowBackgroundColor = getPinnedRowBackgroundColor(data);
        if (isRowTotal(data)) {
            return {
                backgroundColor:
                    pinnedRowBackgroundColor ?? "var(--proda-white-l95)",
                fontWeight: "bold",
                textAlign: "right",
                ...cellStylesWithEllipsis,
            };
        } else if (isRowCategory(data)) {
            return {
                backgroundColor: "var(--proda-white-130)",
                fontWeight: "bold",
                ...cellStylesWithEllipsis,
            };
        }
        return {
            ...cellStylesWithEllipsis,
        };
    };

    const columnDefs =
        editMode === "rows"
            ? [
                  {
                      headerName: "",
                      checkboxSelection: true,
                      width: 40,
                      pinned: "left" as const,
                      lockPosition: true,
                      field: "__checkbox__",
                      colId: "__checkbox__",
                      cellStyle: cellStyleCheckbox,
                  },
                  columnDefOriginalAccountName,
                  columnDefAccount,
                  ...(hasAnySubcategory ? [columnDefSubcategory] : []),
                  ...columnDefsOperatingStatements,
              ]
            : [
                  columnDefOriginalAccountName,
                  columnDefAccount,
                  ...(hasAnySubcategory ? [columnDefSubcategory] : []),
                  ...columnDefsOperatingStatements,
              ];
    return columnDefs;
};
